import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './CustomDropdown.css';

function CustomDropdown({
                          icon: Icon,
                          placeholder,
                          baseClass = 'custom-dropdown',
                          hoverClass,
                          activeClass,
                          borders = '',
                          bordersClassPrefix = 'border-black-',
                          options = [],
                          value,
                          onChange,
                          onFocus,
                          onBlur,
                        }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Generowanie klas dla ramek na podstawie borders
  const borderClasses = borders
      .split('')
      .map((char) => `${bordersClassPrefix}${char}`)
      .join(' ');

  // Filtrowanie opcji na podstawie wyszukiwania
  const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Znalezienie etykiety wybranej opcji
  const selectedLabel = options.find((opt) => opt.value === value)?.label || '';

  // Zamknięcie dropdown przy kliknięciu poza nim
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOptionClick = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    onFocus?.();
  };

  const handleInputBlur = () => {
    onBlur?.();
  };

  const handleMouseEnter = () => {
    if (inputRef.current) {
      inputRef.current.parentElement.classList.add(hoverClass);
    }
  };

  const handleMouseLeave = () => {
    if (inputRef.current) {
      inputRef.current.parentElement.classList.remove(hoverClass);
    }
  };

  return (
      <div
          ref={dropdownRef}
          className={`${baseClass} ${activeClass} ${borderClasses} ${isOpen ? 'dropdown-open' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
      >
        <div className="dropdown-header">
          {Icon && <Icon className="input-icon" />}
          <input
              ref={inputRef}
              type="text"
              className="input-field"
              placeholder={placeholder}
              value={isOpen ? searchTerm : selectedLabel}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onClick={() => setIsOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setIsOpen(true);
                }
              }}
              autoComplete="off"
          />
          <span
            className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
            role="button"
            tabIndex={0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen((prev) => !prev);
              }
            }}
          >
            ▼
          </span>
        </div>

        {isOpen && (
            <div className="dropdown-menu">
              {filteredOptions.length > 0 ? (
                  <ul className="dropdown-list">
                    {filteredOptions.map((option) => (
                        <li
                            key={option.value}
                            className={`dropdown-item ${value === option.value ? 'selected' : ''}`}
                            onClick={() => handleOptionClick(option.value)}
                        >
                          {option.label}
                        </li>
                    ))}
                  </ul>
              ) : (
                  <div className="dropdown-empty">Brak wyników</div>
              )}
            </div>
        )}
      </div>
  );
}

CustomDropdown.propTypes = {
  icon: PropTypes.elementType,
  placeholder: PropTypes.string.isRequired,
  baseClass: PropTypes.string,
  hoverClass: PropTypes.string.isRequired,
  activeClass: PropTypes.string,
  borders: PropTypes.string,
  bordersClassPrefix: PropTypes.string,
  options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

CustomDropdown.defaultProps = {
  baseClass: 'custom-dropdown',
  borders: '',
  bordersClassPrefix: 'border-black-',
  activeClass: '',
  onFocus: () => {},
  onBlur: () => {},
};

export default CustomDropdown;
